import { Badge, Card, Group, ScrollArea, Stack, Text } from "@mantine/core";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import type { SeedingTeam } from "./seeding.types";

interface UnassignedTeamsPanelProps {
  readonly droppableId: string;
  readonly teams: readonly SeedingTeam[];
  readonly saving: boolean;
  readonly maxHeight?: number;
  readonly emptyLabel: string;
}

/**
 * Shared "unassigned teams" column used by both pin tabs (group + slot).
 * Renders a droppable scroll area with draggable team cards and a friendly
 * empty-state once every team has been pinned.
 */
export const UnassignedTeamsPanel = ({
  droppableId,
  teams,
  saving,
  maxHeight = 400,
  emptyLabel,
}: UnassignedTeamsPanelProps) => (
  <Card withBorder>
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fw={600}>Unassigned teams</Text>
        <Badge variant="light" color="gray">
          {teams.length}
        </Badge>
      </Group>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <ScrollArea.Autosize mah={maxHeight}>
            <Stack
              gap="xs"
              ref={provided.innerRef}
              {...provided.droppableProps}
              mih={80}
              style={{
                backgroundColor: snapshot.isDraggingOver
                  ? "var(--mantine-color-dark-6)"
                  : undefined,
                borderRadius: 6,
                padding: 4,
              }}
            >
              {teams.map((team, idx) => (
                <Draggable
                  key={team.id}
                  draggableId={team.id}
                  index={idx}
                  isDragDisabled={saving}
                >
                  {(drag) => (
                    <Card
                      withBorder
                      padding="xs"
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      style={{
                        ...drag.draggableProps.style,
                        cursor: "grab",
                      }}
                    >
                      <Text size="sm" fw={500}>
                        {team.name}
                      </Text>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {teams.length === 0 && (
                <Text size="xs" c="dimmed" ta="center" py="md">
                  {emptyLabel}
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Droppable>
    </Stack>
  </Card>
);
